import { gql, useLazyQuery } from "@apollo/client";
import React from "react";
import { mainApiConfig } from "../../../../configure/mainApiConfig";
import { AsyncLoadOptions, AsyncLoadResult } from "../../../../shared/abstractStore";
import { analyserInterrogationSupportingDataQuery, analyserInterrogationSupportingDataQueryVariables } from "../../generated/analyserInterrogationSupportingDataQuery";
import { questionFieldsFragment } from "../../models/Question";
import { questionResponseFieldsFragment } from "../../models/QuestionResponse";



export function useAnalyserInterrogationSupportingData(questionnaireIds: string[], options: AsyncLoadOptions = {}, includeNonUser: boolean = false,): AsyncLoadResult<analyserInterrogationSupportingDataQuery> {
    // Query the data we need from the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<analyserInterrogationSupportingDataQuery, analyserInterrogationSupportingDataQueryVariables>(
        gql`
        query analyserInterrogationSupportingDataQuery ($questionnaireIds: [ID!], $includeNonUser: Boolean!) {
            questions (questionnaireIds: $questionnaireIds) {
                ...questionFields
            }
            questionResponses (questionnaireIds: $questionnaireIds, includeNonUser: $includeNonUser) {
                ...questionResponseFields
            }
        }
        ${questionFieldsFragment}
        ${questionResponseFieldsFragment}
        `,
        {
            variables: {
                questionnaireIds: questionnaireIds ?? [],
                includeNonUser: includeNonUser
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we're being lazy)
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load]);

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}